import { useState } from "react";
import { Code } from "./Code.js";
import type { Tab } from "../routing.js";

/**
 * 使い方（サポートページ）。
 *
 * ★ **エンドポイントの一覧はここに書かない。** それは「API」タブが
 * `/openapi.json` を読んで描いている。書き写すと三重管理になり、
 * 必ずどれかが古くなる。ここに置くのは**考え方と手順**だけ。
 *
 * ★ 例に出す URL とテナントは**いまの画面のもの**を使う。
 * 読んだ人がそのまま貼れる形にしておかないと、書き換える手間で止まる。
 *
 * ★ SDK の呼び出しが実在することは、テストが型の上で見張る
 *（`support-doc.test.tsx`）。文章だけ残って API が消える事故を防ぐ。
 */

export interface SupportViewProps {
  /** この画面の配信元。例に埋める。 */
  readonly baseUrl: string;
  readonly tenantId: string;
  readonly projectId: string | null;
  /** 本文から別のタブへ送る。 */
  readonly onOpenTab?: (tab: Tab) => void;
}

interface Section {
  readonly id: string;
  readonly title: string;
}

const SECTIONS: readonly Section[] = [
  { id: "overview", title: "この基盤でできること" },
  { id: "place", title: "テナントとプロジェクト" },
  { id: "environment", title: "Environment（実行の設定）" },
  { id: "credential", title: "Credential（LLM の鍵）" },
  { id: "bundle", title: "資産バンドル" },
  { id: "chat", title: "チャットと成果物" },
  { id: "sdk", title: "SDK から使う" },
  { id: "trouble", title: "うまくいかないとき" },
];

function Note({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="doc-note">{children}</p>;
}

export function SupportView({
  baseUrl,
  tenantId,
  projectId,
  onOpenTab,
}: SupportViewProps): React.JSX.Element {
  const scope =
    projectId === null
      ? `{ tenantId: "${tenantId}" }`
      : `{ tenantId: "${tenantId}", projectId: "${projectId}" }`;

  const goto = (tab: Tab, label: string): React.JSX.Element => (
    <button type="button" className="linklike" onClick={() => onOpenTab?.(tab)}>
      {label}
    </button>
  );

  return (
    <section className="stack">
      <header className="row">
        <h2 style={{ margin: 0, fontSize: 16 }}>使い方</h2>
      </header>

      <div className="doc-layout">
        {/* 目次。長い文書なので、頭から読ませない。 */}
        <nav className="doc-toc" aria-label="目次">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`}>
              {s.title}
            </a>
          ))}
        </nav>

        <div className="doc-body stack">
          {/* ---------------------------------------------------------- */}
          <article id="overview" className="stack">
            <h3>この基盤でできること</h3>
            <p>
              Claude Code / Codex を、手元ではなく<strong>サーバ側のサンドボックス</strong>で
              動かします。指示を送ると隔離されたコンテナが起動し、作業して、成果物を返します。
            </p>
            <ul>
              <li>画面から対話する（{goto("chat", "チャット")}）</li>
              <li>プログラムから呼ぶ（下の「SDK から使う」）</li>
              <li>過去の実行を見る（{goto("runs", "Runs")}）</li>
            </ul>
            <Note>
              実行環境・鍵・配る資産は、あらかじめ登録しておきます。
              何も無い状態では実行できません（
              <code>environment_not_found</code> になります）。
            </Note>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="place" className="stack">
            <h3>テナントとプロジェクト</h3>
            <p>
              いちばん外側が<strong>テナント</strong>（＝所属組織）で、その下に
              <strong>プロジェクト</strong>を作れます。画面の上部に、
              いまどちらにいるかが出ています。
            </p>

            <div className="doc-table">
              <table>
                <thead>
                  <tr>
                    <th>いる場所</th>
                    <th>作られるもの</th>
                    <th>見えるもの</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>テナント</td>
                    <td>テナントのもの</td>
                    <td>テナントのものだけ</td>
                  </tr>
                  <tr>
                    <td>プロジェクト</td>
                    <td>そのプロジェクトのもの</td>
                    <td>そのプロジェクト ＋ テナント</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Note>
              見え方は<strong>下向きだけ</strong>です。テナントに置いた鍵はどのプロジェクトからも
              使えますが、プロジェクトに置いた鍵はテナントからも他のプロジェクトからも
              見えません（ID を直に指定しても使えません）。
              案件ごとに鍵を分けたいときにプロジェクトを作ります。
            </Note>

            <p>
              居場所は URL に載るので、そのまま人に渡せます。
              プロジェクトから出るには、上部の「テナント全体へ戻る」を押します。
            </p>
            <Code>{`${baseUrl}/t/${tenantId}/environments          テナントの Environment
${baseUrl}/t/${tenantId}/p/<projectId>/bundles   プロジェクトの資産バンドル`}</Code>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="environment" className="stack">
            <h3>Environment（実行の設定）</h3>
            <p>
              どの Runner（claude-code / codex）で、どのモデルで、どの鍵を使って、
              どの資産を配って動かすか——を 1 つにまとめたものです。
              {goto("environments", "Environment")} 画面で作ります。
            </p>

            <h4>重なって効く</h4>
            <p>
              テナント → プロジェクト → 自分、の順に<strong>重なります</strong>。
              上位に共通の設定を置き、下位で違うところだけ書きます。
            </p>
            <Code>{`t:${tenantId}                     ← 共通（鍵・サンドボックス）
  └ t:${tenantId}/p:<projectId>   ← この案件だけ model を変える`}</Code>

            <h4>同じ場所に複数置いて選び分ける</h4>
            <p>
              同じスコープに複数作れます。チャット画面の上部の
              「Environment」で選ぶと、その会話はそれで走ります。
              <strong>選んでも上位の設定は効いたまま</strong>です。
            </p>
            <Note>
              「（自動）」のままなら、これまでどおり全部が重なります。
              いま何が効くかは Environment 画面の「実効設定」で確かめられます。
            </Note>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="credential" className="stack">
            <h3>Credential（LLM の鍵）</h3>
            <p>
              Anthropic / OpenAI / Vertex などの鍵を{goto("credentials", "Credential")}
              画面で登録します。登録した値は<strong>二度と読み出せません</strong>
              （書き込み専用）。
            </p>
            <Note>
              この基盤にログインするためのキー（<code>rca_</code> で始まる）とは<strong>別物</strong>です。
              取り違えると「credential が見つかりません」になります。
            </Note>
            <p>
              git の認証情報や、外部 API を MCP として使うための鍵（
              <code>api-key</code>）もここに置きます。
            </p>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="bundle" className="stack">
            <h3>資産バンドル</h3>
            <p>
              エージェントに配るものをまとめた入れ物です（
              {goto("bundles", "資産バンドル")}）。Environment から参照して配ります。
            </p>
            <ul>
              <li>
                <strong>instructions</strong> — <code>CLAUDE.md</code> /{" "}
                <code>AGENTS.md</code> として置かれる決めごと
              </li>
              <li>
                <strong>Skill</strong> — 手順書。<code>SKILL.md</code> と付随ファイル
              </li>
              <li>
                <strong>subagent</strong> — 呼び出せる別人格。Runner ごとに定義を分けます
                （モデルも権限も対応しないため）
              </li>
              <li>
                <strong>MCP</strong> — 外部ツールの接続先
              </li>
              <li>
                <strong>アダプタ</strong> — MCP を提供していない REST API を MCP として見せる定義
              </li>
            </ul>
            <Note>
              更新は<strong>差分</strong>です。触ったところだけ送るので、
              手元に無いものが消えることはありません。
              既にあるものは「一覧 → 選ぶ → 編集」で直せます。
            </Note>
            <Note>
              バンドルは<strong>暗号化されません</strong>。鍵をそのまま書かず、
              Credential に登録して <code>credentialId</code> で参照してください
              （そのまま書くと登録時に弾かれます）。
            </Note>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="chat" className="stack">
            <h3>チャットと成果物</h3>
            <p>
              指示を送ると Run が 1 つ作られます。進捗はそのまま流れ、
              走っている間は状態表示の枠が光ります。
            </p>

            <h4>会話を続ける</h4>
            <p>
              「セッションを選ぶ」から作ると、同じ作業場所とやり取りの履歴が引き継がれます。
              セッションを使わない場合、Run ごとに真っさらな場所で始まります。
            </p>

            <h4>成果物の受け取り</h4>
            <p>
              ★ 取り出せるのは <code>/workspace/out/</code> の下に置かれたものだけです。
              エージェントには自動でそう指示してあります。
            </p>
            <Note>
              作業場所の全部は出しません。中間ファイルや、うっかり置かれた秘密まで
              持ち出さないためです。「成果物が出てこない」ときは、
              まず <code>out/</code> に置かれているかを確かめてください。
            </Note>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="sdk" className="stack">
            <h3>SDK から使う</h3>
            <p>
              <code>@rca/sdk</code> は TypeScript の薄い包みです。
              <strong>実行時依存はありません</strong>。<code>fetch</code> と Web 標準だけを使うので、
              Node / ブラウザ / Cloudflare Workers で同じに動きます。
            </p>

            <h4>1. 鍵を用意する</h4>
            <p>
              {goto("keys", "API キー")}画面で発行します。発行時にしか表示されないので、
              その場で控えてください。
            </p>
            <Note>
              画面のログインには使えません（画面はシングルサインオンだけです）。
              API を叩くためのものです。Zitadel のトークンでも同じ API を叩けます。
            </Note>

            <h4>2. つなぐ</h4>
            <Code>{`import { RcaClient } from "@rca/sdk";

const rca = new RcaClient({
  baseUrl: "${baseUrl}",
  // 呼び出しごとに読む。差し替えや更新に追随するため。
  getToken: () => process.env.RCA_API_KEY ?? null,
});`}</Code>

            <h4>3. 実行して待つ</h4>
            <Code>{`const result = await rca.run({
  prompt: "src/ のテストを全て通るようにして",
  scope: ${scope},
  onEvent: (event) => console.log(event.type),
});

console.log(result.run.status);  // "succeeded" / "failed" / …
console.log(result.finalText);   // 最終メッセージ`}</Code>
            <Note>
              進捗は SSE で受け取り、切れたら続きから読み直します。
              <strong>最終状態は必ず取り直す</strong>ので、
              購読が途切れても結果を取り違えません。
              SSE が使えない環境なら <code>withoutEvents: true</code> を渡します。
            </Note>

            <h4>Environment を選ぶ</h4>
            <Code>{`// 同じ場所に複数置いた Environment を名指しする。
// 上位（テナント）の設定は重なったまま。
await rca.run({
  prompt: "軽い仕事なので速いモデルで",
  scope: ${scope},
  environmentId: "env_fast",
});

// いま何が効くかを、実行せずに確かめる。
const resolved = await rca.environments.resolve(${scope}, { environmentId: "env_fast" });
console.log(resolved.chain);       // 重なった順
console.log(resolved.provenance);  // どの項目がどこ由来か`}</Code>

            <h4>会話を続ける</h4>
            <Code>{`const session = await rca.sessions.create({
  scope: ${scope},
  runnerType: "claude-code",
});

await rca.run({ prompt: "1 通目", sessionId: session.id, scope: ${scope} });
await rca.run({ prompt: "続きをお願い", sessionId: session.id, scope: ${scope} });

const history = await rca.history(session.id);  // 過去のやり取り（古い順）`}</Code>
            <Note>
              セッションは使い終わったら <code>rca.sessions.delete(id)</code> で畳んでください。
              放っておくとコンテナが空回りし、費用になります
              （一定時間で自動的に片付きますが、明示する方が確実です）。
            </Note>

            <h4>ファイルを渡す</h4>
            <Code>{`const upload = await rca.upload(file);  // File / Blob / Uint8Array / ReadableStream
await rca.run({
  prompt: "この資料を読んで要約して",
  uploads: [upload.uploadId],
  scope: ${scope},
});`}</Code>
            <Note>
              単発で送るか 3 段に分けるかは<strong>SDK が選びます</strong>。
              大きいファイルは Worker が動く前に弾かれるので、
              上限で断られたら自動で分割してやり直します。
            </Note>

            <h4>成果物を取り出す</h4>
            <Code>{`const files = await rca.runs.workspace(result.run.id);
for (const file of files.items) console.log(file.path, file.size);

const body = await rca.runs.workspaceFile(result.run.id, "out/report.md");`}</Code>

            <h4>プロジェクトの中のものを扱う</h4>
            <Code>{`// projectId は絞り込みではなく「居場所」。
// 付けると、そのプロジェクトのもの ＋ テナントのものが返る。
await rca.credentials.list({ projectId: "prj_..." });
await rca.bundles.list({ projectId: "prj_..." });
await rca.environments.list({ projectId: "prj_..." });`}</Code>

            <h4>失敗したとき</h4>
            <Code>{`import { RcaApiError } from "@rca/sdk";

try {
  await rca.run({ prompt: "…", scope: ${scope} });
} catch (cause) {
  if (cause instanceof RcaApiError) {
    console.error(cause.problem.code);       // 機械が見る識別子
    console.error(cause.problem.detail);     // 人が読む説明
    if (cause.problem.retriable) { /* 送り直してよい */ }
  }
}`}</Code>
            <Note>
              GET / PUT / DELETE は自動で再試行します。POST は
              <strong>冪等キーを渡したときだけ</strong>再試行します
              （応答前に切れた場合、サーバ側では成功しているかもしれず、
              盲目的に送り直すと Run が二重になるため）。
              <code>rca.run()</code> は内部で冪等キーを付けています。
            </Note>

            <p>
              個々のエンドポイントの引数と応答は{goto("api", "API")}タブで見られます
              （サーバの仕様書をそのまま描いています）。
            </p>
          </article>

          {/* ---------------------------------------------------------- */}
          <article id="trouble" className="stack">
            <h3>うまくいかないとき</h3>

            <div className="doc-table">
              <table>
                <thead>
                  <tr>
                    <th>出るもの</th>
                    <th>たいていの原因</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="mono">environment_not_found</td>
                    <td>
                      Environment が 1 つも無い。{goto("environments", "Environment")}
                      画面で作ります
                    </td>
                  </tr>
                  <tr>
                    <td>credential がここからは利用できません</td>
                    <td>
                      別のプロジェクト（またはテナント直下）の鍵を指している。
                      いまの居場所から見える鍵だけが使えます
                    </td>
                  </tr>
                  <tr>
                    <td>credential が見つかりません</td>
                    <td>
                      LLM の鍵（<code>sk-…</code>）とこの基盤のキー（<code>rca_…</code>）の取り違え
                    </td>
                  </tr>
                  <tr>
                    <td>成果物が出てこない</td>
                    <td>
                      <code>/workspace/out/</code> の外に置かれている
                    </td>
                  </tr>
                  <tr>
                    <td>プロジェクトを選ぶ欄が出ない</td>
                    <td>
                      プロジェクトがまだ 1 つも無い。{goto("projects", "プロジェクト")}
                      画面で作ると出ます
                    </td>
                  </tr>
                  <tr>
                    <td>バンドルを直したのに反映されない</td>
                    <td>
                      Environment がそのバンドルを参照していない。
                      参照は Environment 側で指定します
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Note>
              それでも分からないときは{goto("logs", "ログ")}を見てください。
              Run ごとに、拒否された理由と解決された設定が残っています。
            </Note>
          </article>
        </div>
      </div>
    </section>
  );
}
